<template>
  <div :class="{ ImageBackground: !disabled }">
    <picture v-if="!disabled" class="ImageBackground-picture">
      <source
        v-for="source of computedSources"
        :key="source.src"
        :srcset="source.src"
        :media="source.media"
        :type="source.type"
      >
      <img
        :src="fallbackSrc"
        :alt="alt"
        class="ImageBackground-image"
        :style="imageStyles"
      >
    </picture>
    <div class="ImageBackground-content">
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

export interface ImageSource {
  src: string;
  media?: string;
  type?: string;
}

export interface ImageBackgroundProps {
  /** Image sources with optional media queries and types. Last source is used as fallback. */
  sources: ImageSource[];
  /** Alt text for accessibility (empty string for decorative images) */
  alt?: string;
  /** Disable background image functionality */
  disabled?: boolean;
  /** Horizontal padding. Can be overridden via the `--space-x` CSS custom property (e.g. from a tenant-themed SCSS token). */
  spaceX?: string;
  /** Top padding. Can be overridden via the `--space-top` CSS custom property. */
  spaceTop?: string;
  /** Bottom padding. Can be overridden via the `--space-bottom` CSS custom property. */
  spaceBottom?: string;
  /** Sizes the image to this content height instead of the rendered one, so content can grow past the image. */
  contentHeight?: string;
  /** How the image should fit (cover or contain) */
  objectFit?: 'cover' | 'contain';
  /** Image position */
  objectPosition?: string;
}

const props = withDefaults(defineProps<ImageBackgroundProps>(), {
  alt: '',
  disabled: false,
  spaceX: '0px',
  spaceTop: '0px',
  spaceBottom: '0px',
  objectFit: 'cover',
  objectPosition: 'center',
});

const computedSources = computed<ImageSource[]>(() => {
  // Filter sources with media queries for <source> elements
  return props.sources.filter(source => source.media || source.type);
});

// Use the first source without media query as fallback, or the last source
const fallbackSrc = computed<string>(() => {
  const fallback = props.sources.find(source => !source.media && !source.type);
  return fallback?.src ?? props.sources[props.sources.length - 1]?.src ?? '';
});

const imageStyles = computed(() => ({
  objectFit: props.objectFit,
  objectPosition: props.objectPosition,
}));

// Without an explicit content height the picture spans the whole wrapper (content + top padding).
// Reads the live --top-padding/--bottom-padding vars, so a CSS-level override of those stays in sync.
const pictureHeight = computed<string>(() => (props.contentHeight
  ? `calc(${props.contentHeight} + var(--top-padding) - var(--bottom-padding))`
  : 'calc(100% - var(--bottom-padding))'));
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;

@layer cvo-ui {
  .ImageBackground {
    // The --space-* vars let a consumer override the spacing from SCSS (theme-aware tokens) without touching props.
    --side-padding: var(--space-x, v-bind("spaceX"));
    --top-padding: var(--space-top, v-bind("spaceTop"));
    --bottom-padding: var(--space-bottom, v-bind("spaceBottom"));
    --picture-height: v-bind("pictureHeight");

    position: relative;
    padding: 0 var(--side-padding);
    padding-bottom: var(--top-padding);
    margin-bottom: 30px;

    &-picture {
      position: absolute;
      inset: 0;
      bottom: var(--bottom-padding);
      width: 100%;
      height: var(--picture-height);
      border-radius: vars.$border-radius-md; // TODO: border-radius in figma component ElementOverImg is linked to the context (search trip, confirmation) and will be changed
      overflow: hidden;
    }

    &-image {
      width: 100%;
      height: 100%;
      display: block;
    }

    &-content {
      position: relative;
      transform: translateY(var(--top-padding));
    }

    @container (max-width: #{vars.$screen-md-max-width}) {
      --side-padding: 10px;
    }
  }
}
</style>